import { useEffect, useMemo, useState } from 'react';

import { useCallbackWhenDomChanges } from './useCallbackWhenDomChanges';
import { ElementSelector, queryElement } from '../selectors';

/**
 * Queries elements that might be added to the page later until they are all found
 *
 * @param selectors - element selectors
 * @param continuouslyWatch - when true, keeps observing DOM mutations even after elements are found.
 *                            Enable for dynamically repositioned or remounted elements.
 */
export function useDeferredElements(
  selectors: ElementSelector[],
  continuouslyWatch = false
): HTMLElement[] {
  const [elements, setElements] = useState<HTMLElement[]>([]);

  useCallbackWhenDomChanges(
    () => setElements(selectors.map(queryElement).filter((it) => !!it)),
    elements.length !== selectors.length || continuouslyWatch
  );

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setElements(selectors.map(queryElement).filter((it) => !!it));
  }, [selectors]);

  return elements;
}

/**
 * Queries an element that might be added to the page later
 *
 * @param selector - element selector
 * @param continuouslyWatch - when true, keeps observing DOM mutations even after element is found.
 *                            Enable for dynamically repositioned or remounted elements.
 */
export function useDeferredElement(
  selector: ElementSelector,
  continuouslyWatch = false
): HTMLElement | null {
  const selectors = useMemo(() => [selector], [selector]);
  const elements = useDeferredElements(selectors, continuouslyWatch);
  return elements[0] ?? null;
}
